/**
 * "We are working on the platform" (#3561, BE ourteam-backend#4630).
 *
 * Shown to customers, and NOT in the admin console. The notice is addressed to
 * the people affected by a window; the people running it turned it on and are
 * not blocked by it, so a banner over an app that still works for them would be
 * saying something untrue about the screen it sits on.
 *
 * Read from the PUBLIC endpoint, which needs no session — the audience is
 * mostly people who are not admins. A failed or unreadable read renders
 * nothing: a banner is not worth an error state, and the wrong default here
 * would put a false outage notice in front of every customer.
 */
import { useEffect } from "react";
import { AlertTriangleIcon } from "lucide-react";
import { useMaintenance, isPlatformAdmin } from "@context/maintenance";
import { useUserProfileContext } from "@context/user_profile";
import { formatWindow } from "@page/admin-maintenance/components/window-format";
import { MAINTENANCE_BLOCKED_EVENT } from "@util/maintenance-guard";
import { useSnackbar } from "@hook/use-snackbar";

export default function MaintenanceBanner() {
    const { state } = useMaintenance();
    const [, userInfo] = useUserProfileContext();
    const snackbar = useSnackbar();

    /**
     * One message for every request the guard turned away.
     *
     * Listened for here rather than raised at each call site: a bulk action can
     * fire a dozen requests, and a dozen identical snackbars would bury the one
     * sentence that explains them. The effect is above the early returns on
     * purpose — hooks must run unconditionally, and this must keep working on
     * screens where the banner itself renders nothing.
     */
    useEffect(() => {
        const onBlocked = () => {
            snackbar.showError(
                "Changes are paused",
                "Scheduled maintenance is in progress. Please try again once it finishes.",
            );
        };
        window.addEventListener(MAINTENANCE_BLOCKED_EVENT, onBlocked);
        return () => window.removeEventListener(MAINTENANCE_BLOCKED_EVENT, onBlocked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!state?.is_active) return null;
    // Not in the admin console. This notice is addressed to customers, and the
    // people running the maintenance do not need to be told it is happening —
    // they turned it on. Their controls are not blocked either (see
    // MaintenanceProvider), so a banner over an app that still works would be
    // saying something untrue about the screen it sits on.
    if (isPlatformAdmin(userInfo?.role)) return null;

    // Rendered by the same helper the notice email uses, so a window inside one
    // day collapses to "5:58 AM – 7:00 AM" instead of repeating the date and the
    // timezone on both sides of an "until". An absent end time is still stated
    // as absent: rendering a finish we were never given would turn a
    // maintenance notice into a promise nobody made.
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const windowText = state.starts_at
        ? state.ends_at
            ? formatWindow(state.starts_at, state.ends_at, viewerZone)
            : `From ${formatWindow(state.starts_at, null, viewerZone)}`
        : null;

    return (
        <div
            role="status"
            className="flex items-start gap-2.5 border-b border-amber-200 bg-amber-50 px-4 py-2.5 sm:px-6"
        >
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div className="min-w-0 text-sm text-amber-900">
                <span className="font-medium">Scheduled maintenance in progress.</span>{" "}
                {state.message ? <span>{state.message}</span> : null}
                {windowText && (
                    <span className="block text-xs text-amber-800 sm:mt-0.5">{windowText}</span>
                )}
            </div>
        </div>
    );
}
